/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Friday, 5th April 2019
 *
 * Manages the state of the Portal Ui
 */
import React from 'react';
import Button from '@material-ui/core/Button';
import * as Sentry from '@sentry/browser';
import { configure, observable, decorate, action } from 'mobx';
configure({ enforceActions: 'observed' });

export default class PortalStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.theme = new Theme(this);
    this.snackbar = new Snackbar(this);
    this.sidebar = new Sidebar(this);
    this.loading = new Loading(this);
    this.error = new Error(this);
  }
}

class Theme {
  themeType = 'light'; // Default is light
  constructor(portalStore) {
    this.portalStore = portalStore;

    // TODO: Add check for authUser and set their profile theme preference over local storage.
    if (localStorage.getItem('themeType') !== '' || localStorage.getItem('themeType' !== undefined)) {
      this.getThemeFromLocal();
    }

    // Set local storage to default if no theme exists
    this.setThemeToLocal();
  }
  toggleThemeType = () => {
    this.getThemeFromLocal();
    this.themeType = this.themeType === 'light' ? 'dark' : 'light';
    this.setThemeToLocal();
  };
  setThemeToLocal = () => {
    localStorage.setItem('themeType', this.themeType);
  };
  getThemeFromLocal = () => {
    if (localStorage.getItem('themeType') !== null) this.themeType = localStorage.getItem('themeType');
  };
}
decorate(Theme, {
  themeType: observable,
  toggleThemeType: action,
  setThemeToLocal: action,
  getThemeFromLocal: action,
});

class Snackbar {
  displayed = [];
  snackbars = [];

  constructor(portalStore) {
    this.portalStore = portalStore;
  }
  enqueueSnackbar = note => {
    this.snackbars.push({
      key: new Date().getTime() + Math.random(),
      ...note,
    });
  };
  removeSnackbar = note => {
    this.snackbars.remove(note);
  };

  showSnackbarWithDismiss = (message, variant, milliseconds) => {
    this.enqueueSnackbar({
      message: message,
      options: {
        variant: variant ? variant.toLowerCase() : 'info',
        autoHideDuration: milliseconds ? milliseconds : 6500,
        action: <Button size='small'>{'Dismiss'}</Button>,
      },
    });
  };

  showSnackbarWithActionCallback = (message, variant, buttonName, actionCallback) => {
    // Verify cb is a function
    const callback = typeof actionCallback === 'function' ? actionCallback : null;
    this.enqueueSnackbar({
      message: message,
      options: {
        variant: variant ? variant.toLowerCase() : 'info',
        autoHideDuration: 7000,
        action: <Button size='small'>{buttonName}</Button>,
        onClickAction: callback,
      },
    });
  };

  showSnackbarPasswordReset = (message, variant, email) => {
    // Verify cb is a function
    this.enqueueSnackbar({
      message: message,
      options: {
        variant: variant ? variant.toLowerCase() : 'info',
        autoHideDuration: 10000,
        action: (
          <Button size='small' onClick={() => this.portalStore.rootStore.loginStore.passwordReset(email)}>
            Resend Email
          </Button>
        ),
      },
    });
  };

  showSnackbarWithVariant = (message, variant) => {
    this.enqueueSnackbar({
      message: message,
      options: {
        variant: variant ? variant.toLowerCase() : 'info',
        autoHideDuration: 2000,
      },
    });
  };

  showSnackbarWithVariantWithDuration = (message, variant, duration) => {
    this.enqueueSnackbar({
      message: message,
      options: {
        variant: variant ? variant.toLowerCase() : 'info',
        autoHideDuration: duration,
      },
    });
  };
}
decorate(Snackbar, {
  snackbars: observable,
  enqueueSnackbar: action,
  removeSnackbar: action,
  showSnackbarWithVariant: action,
  showSnackbarWithDismiss: action,
  showSnackbarWithActionCallback: action,
  showSnackbarWithVariantWithDuration: action,
  showSnackbarLinkToLogin: action,
});

class Sidebar {
  sidebarOpen = false;
  sidebarMini = false;
  constructor(portalStore) {
    this.portalStore = portalStore;
  }
  toggleSidebar = () => {
    this.sidebarOpen = !this.sidebarOpen;
  };
  toggleSidebarMini = () => {
    this.sidebarMini = !this.sidebarMini;
  };
}
decorate(Sidebar, {
  sidebarOpen: observable,
  sidebarMini: observable,
  toggleSidebar: action,
  toggleSidebarMini: action,
});

class Loading {
  isPageLoading = true;
  isViewLoading = true;
  constructor(portalStore) {
    this.portalStore = portalStore;
  }

  hidePageLoader = () => {
    this.isPageLoading = false;
  };
  showPageLoader = () => {
    this.isPageLoading = true;
  };
  toggleViewLoading = () => {
    this.isViewLoading = !this.isViewLoading;
  };
}
decorate(Loading, {
  isPageLoading: observable,
  isViewLoading: observable,
  hidePageLoader: action,
  showPageLoader: action,
  toggleViewLoading: action,
});

class Error {
  sentryEnabled = process.env.REACT_APP_SENTRY;

  constructor(portalStore) {
    this.portalStore = portalStore;
  }

  handleError = error => {
    if (this.sentryEnabled) {
      // Sentry Scope
      Sentry.configureScope(scope => {
        // User (Auth or Anonymous)
        if (this.portalStore.rootStore.sessionStore.authUser) {
          scope.setTag('userId', this.portalStore.rootStore.sessionStore.authUser.id);
          scope.setTag('userEmail', this.portalStore.rootStore.sessionStore.authUser.email);
        } else {
          scope.setTag('user', 'anonymous');
        }
        // Session ID
        scope.setTag('sessionId', this.portalStore.rootStore.sessionStore.id);
        // Thumbprint
        scope.setTag('thumbprint', this.portalStore.rootStore.deviceStore.thumbprint);
        // Language
        scope.setTag('language', this.portalStore.rootStore.deviceStore.language);
        // Browser
        scope.setTag('browser', this.portalStore.rootStore.deviceStore.browser);
        // Engine
        scope.setTag('engine', this.portalStore.rootStore.deviceStore.engine);
        // OS
        scope.setTag('os', this.portalStore.rootStore.deviceStore.os);
        // Plugins
        scope.setTag('plugins', this.portalStore.rootStore.deviceStore.plugins);
      });
      Sentry.captureException(error);
    }
  };
}
decorate(Error, {
  sentryEnabled: observable,
  handleError: action,
});
